import React from "react";
import { Phone, MapPin, Clock } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";
import PageHead from "@/components/PageHead";
import ContactForm from "@/components/ContactForm";

const DEPARTMENT_NOTES = [
  {
    t: "Sales",
    d: "New partner inquiries — carriers, agencies, IMOs/FMOs, installers and contractors.",
  },
  {
    t: "Media buying",
    d: "Active partners scaling spend, opening new geographies or adding product types.",
  },
  {
    t: "Compliance",
    d: "TCPA, CCPA, GLBA, CMS audit requests and consent retention questions.",
  },
  {
    t: "Press & media",
    d: "Interviews, partnership stories and speaking engagements.",
  },
];

export default function Contact() {
  return (
    <main data-testid="contact-page">
      <PageHead
        title="Contact"
        description="Open a B2B media buy with Citirex. Partnerships desk for carriers, agencies, IMOs, FMOs and contractors — Sherman Oaks, Los Angeles."
        path="/contact"
      />

      <section className="border-b border-citirex-border bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-12 md:px-12 md:pb-24 md:pt-20">
          <Reveal>
            <p className="overline">Contact</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 max-w-5xl font-display text-5xl font-semibold leading-[0.95] tracking-tighter md:text-7xl lg:text-[110px]">
              Open a{" "}
              <span className="font-editorial italic font-normal text-citirex-accent">
                media buy
              </span>
              .
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-citirex-sub md:text-xl">
              Citirex is a B2B agency. We work directly with carriers,
              agencies, IMOs, FMOs and contractors. We don't capture consumer
              traffic from this site — reach our partnerships desk below.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-citirex-bg">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 md:grid-cols-12 md:gap-16 md:px-12 md:py-32">
          <div className="md:col-span-7">
            <p className="overline">Partnerships desk</p>
            <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] md:text-5xl">
              Tell us about your{" "}
              <span className="font-editorial italic">appetite</span>.
            </h2>
            <p className="mt-4 max-w-xl font-body text-base text-citirex-sub md:text-lg">
              Pick a department, leave the details, and the right buyer on
              our team will reply within one business day.
            </p>

            <div className="mt-10">
              <ContactForm />
            </div>
          </div>

          <aside className="md:col-span-5">
            <div className="grid grid-cols-1 gap-px border border-citirex-border bg-citirex-border">
              <a
                href="tel:+18188532332"
                data-testid="contact-phone"
                className="group flex items-start gap-4 bg-citirex-surface p-8 transition-colors hover:bg-citirex-bg2"
              >
                <Phone size={28} className="mt-1 text-citirex-accent" />
                <div className="flex-1">
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-citirex-sub">
                    Phone
                  </p>
                  <p className="mt-2 font-display text-xl font-medium text-citirex-ink">
                    (818) 853-2332
                  </p>
                  <p className="mt-1 font-body text-xs text-citirex-sub">
                    Direct partnerships line.
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-4 bg-citirex-surface p-8">
                <MapPin size={28} className="mt-1 text-citirex-accent" />
                <div className="flex-1">
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-citirex-sub">
                    Headquarters
                  </p>
                  <p className="mt-2 font-display text-xl font-medium leading-snug text-citirex-ink">
                    15165 Ventura Blvd, Suite 225
                    <br />
                    Sherman Oaks, CA 91403
                  </p>
                  <p className="mt-1 font-body text-xs text-citirex-sub">
                    Visits by appointment only.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-citirex-surface p-8">
                <Clock size={28} className="mt-1 text-citirex-accent" />
                <div className="flex-1">
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-citirex-sub">
                    Hours
                  </p>
                  <p className="mt-2 font-display text-xl font-medium text-citirex-ink">
                    Mon–Fri · 8a–6p PT
                  </p>
                  <p className="mt-1 font-body text-xs text-citirex-sub">
                    Pacific Time, Los Angeles.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 border border-citirex-border bg-citirex-bg2 p-8">
              <p className="overline">Departments</p>
              <ul className="mt-6 space-y-5 border-t border-citirex-border pt-6">
                {DEPARTMENT_NOTES.map((d) => (
                  <li key={d.t}>
                    <p className="font-display text-base font-medium text-citirex-ink">
                      {d.t}
                    </p>
                    <p className="mt-1 font-body text-xs text-citirex-sub">
                      {d.d}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 border border-citirex-border bg-citirex-ink p-8 text-citirex-inv">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-citirex-inv/60">
                Looking to buy a personal policy?
              </p>
              <p className="mt-3 font-body text-sm text-citirex-inv/80">
                Citirex is a B2B lead generation agency — we don't sell
                insurance directly to consumers. If you're shopping for
                coverage, please contact a licensed agent in your state.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
